import { User, SortDirectionEnum } from "@/__generated__/types";
import { DataTable } from "@/components/DataTable";
import { Column, ColumnDef, Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { formatDate } from "@/lib/formatDate";
import { useLocale } from "@/hooks/useLocale";
import {
  UserOrderingInput,
  UserRoleEnum,
  UserSortableFieldEnum,
} from "@/__generated__/graphql";
import { formatPhoneNumber } from "@/util/phoneUtils";
import { TeamTableActions } from "../TeamTableActions";
import { NameAndAvatar } from "@/components/StudentListWrapper/Grid/NameAndAvatar";
import { useOrg } from "@/hooks/useOrg";
import { useQuery } from "@apollo/client";
import { CURRENT_USER_PERMISSIONS } from "@/api/queries/currentUserPermissions";
import { Badge } from "@/components/ui";

const DATE_FORMAT = "PPP";

const canSort = (field: string): boolean => {
  return Object.values(UserSortableFieldEnum).includes(
    field as UserSortableFieldEnum,
  );
};

const MAP_SORTING_FIELD_TO_COLUMN_ID: Record<string, UserSortableFieldEnum> = {
  name: UserSortableFieldEnum.Name,
  role: UserSortableFieldEnum.Role,
  lastActiveAt: UserSortableFieldEnum.LastActiveAt,
};

export const ROLE_MAP = {
  [UserRoleEnum.Counselor]: "teamManagement.table.counselor",
  [UserRoleEnum.Influencer]: "teamManagement.table.influencer",
};

export function useTeammateTableConfig() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { org } = useOrg();
  const { data } = useQuery(CURRENT_USER_PERMISSIONS, {
    variables: { organizationId: org?.id ?? "" },
    skip: !org?.id,
  });
  const [ordering, setOrdering] = useState<UserOrderingInput[]>([
    {
      field: UserSortableFieldEnum.Name,
      direction: SortDirectionEnum.Asc,
    },
  ]);

  const handleSortingChange = ({
    orderingField,
    orderingDirection,
  }: {
    orderingField: string;
    orderingDirection: SortDirectionEnum;
  }) => {
    setOrdering([
      {
        field: MAP_SORTING_FIELD_TO_COLUMN_ID[orderingField],
        direction: orderingDirection,
      },
    ]);
  };

  const columnsConfig: ColumnDef<User | null>[] = [
    {
      id: "name",
      accessorFn: (row: User | null) => row?.name ?? "",
      enableSorting: canSort("name"),
      header: ({ column }: { column: Column<User | null> }) => (
        <DataTable.THeaderCellSorting
          column={column}
          onSortingChange={handleSortingChange}
        >
          {t("teamManagement.table.name")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<User | null> }) => {
        return (
          <DataTable.TBodyCell>
            <NameAndAvatar
              firstName={row.original?.firstName}
              lastName={row.original?.lastName}
              imageUrl={{ url: row.original?.profilePhoto?.url || undefined }}
              avatarSize={"size-6"}
            />
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "role",
      accessorFn: (row: User | null) => t(ROLE_MAP[row?.role as UserRoleEnum]),
      enableSorting: canSort("role"),
      enableColumnFilter: true,
      meta: {
        label: t("teamManagement.table.role"),
      },
      header: ({ column }: { column: Column<User | null> }) => (
        <DataTable.THeaderCellSorting
          column={column}
          onSortingChange={handleSortingChange}
        >
          {t("teamManagement.table.role")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<User | null> }) => {
        return (
          <DataTable.TBodyCell>
            <div className="flex flex-row gap-4">
              {t(ROLE_MAP[row.original?.role as UserRoleEnum])}
              {row.original?.isDefaultCounselor && (
                <Badge className="rounded-full border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-300 dark:bg-orange-100 dark:text-orange-800">
                  {t("teamManagement.table.default")}
                </Badge>
              )}
            </div>
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "phone",
      accessorFn: (row: User | null) => row?.phoneNumber ?? "",
      enableSorting: canSort("phone"),
      meta: {
        label: t("teamManagement.table.phone"),
      },
      header: () => (
        <DataTable.THeaderCell>
          {t("teamManagement.table.phone")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<User | null> }) => {
        return (
          <DataTable.TBodyCell>
            {formatPhoneNumber(row.original?.phoneNumber)}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "email",
      accessorFn: (row: User | null) => row?.email ?? "",
      enableSorting: canSort("email"),
      meta: {
        label: t("teamManagement.table.email"),
      },
      header: () => (
        <DataTable.THeaderCell>
          {t("teamManagement.table.email")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<User | null> }) => {
        return <DataTable.TBodyCell>{row.original?.email}</DataTable.TBodyCell>;
      },
    },
    {
      id: "lastActiveAt",
      accessorFn: (row: User | null): string => {
        const { lastActiveAt } = row || {};
        return lastActiveAt
          ? formatDate(lastActiveAt, DATE_FORMAT, {
              locale,
            })
          : "---";
      },
      enableSorting: canSort("lastActiveAt"),
      meta: {
        label: t("teamManagement.table.lastLogin"),
      },
      header: ({ column }: { column: Column<User | null> }) => (
        <DataTable.THeaderCellSorting
          column={column}
          onSortingChange={handleSortingChange}
        >
          {t("teamManagement.table.lastLogin")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ renderValue }) => {
        return (
          <DataTable.TBodyCell>{renderValue() as string}</DataTable.TBodyCell>
        );
      },
    },
    {
      id: "ACTIONS",
      header: "",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }: { row: Row<User | null> }) => (
        <div className="h-9">
          {data?.me?.isSuperCounselor && (
            <TeamTableActions row={row} orgId={org?.id} />
          )}
        </div>
      ),
    },
  ];

  return { columns: columnsConfig, ordering };
}
